import { useEffect, useMemo, useRef } from "react";
import {
  Controller,
  type Control,
  type UseFormGetValues,
  type UseFormSetValue,
} from "react-hook-form";

import FormInputError from "components/Form/Input/Error";
import { STYLE_INPUT } from "components/Form/Input/consts";

import { asMoney } from "utils/calcs";
import { PAID_TO_CUSTOMER, TD_STYLE, TH_STYLE } from "../consts";
import type {
  CashOutRow,
  ConsolidationItem,
  DebtConsolidationFormValues,
} from "../types";
import { getDefaultSettlePurpose } from "../utils";

type Props = {
  control: Control<DebtConsolidationFormValues>;
  getValues: UseFormGetValues<DebtConsolidationFormValues>;
  setValue: UseFormSetValue<DebtConsolidationFormValues>;
  creditProviders: ConsolidationItem[];
  loanAmount: number;
  totalConsolidated: number;
  remainingCash: number;
  leftOverCashPurpose: string;
};

export default function CashOutSection({
  control,
  getValues,
  setValue,
  creditProviders,
  loanAmount,
  totalConsolidated,
  remainingCash,
  leftOverCashPurpose,
}: Props) {
  /** Once a user edits a purpose field, we never auto-reapply `defaultPurpose` (e.g. after clearing). */
  const purposeEditedByUserRef = useRef<Set<string>>(new Set());

  const cashOutRows: CashOutRow[] = useMemo(() => {
    const cashToCustomerItems = creditProviders.filter(
      (c) => c.paidTo === PAID_TO_CUSTOMER,
    );

    const settlementCashOutRows: CashOutRow[] = cashToCustomerItems.map(
      (c) => ({
        key: "settle-" + c.uuid,
        defaultPurpose: getDefaultSettlePurpose(c),
        amount: Number(c.settlementAmount || 0),
        isRemaining: false,
      }),
    );

    const includeRemainingCashOutRow =
      remainingCash > 0 ||
      (settlementCashOutRows.length === 0 && loanAmount > totalConsolidated);

    if (includeRemainingCashOutRow) {
      return [
        ...settlementCashOutRows,
        {
          key: "remaining",
          defaultPurpose: leftOverCashPurpose,
          amount: Math.max(0, remainingCash),
          isRemaining: true,
        },
      ];
    }
    return settlementCashOutRows;
  }, [
    creditProviders,
    leftOverCashPurpose,
    loanAmount,
    remainingCash,
    totalConsolidated,
  ]);

  /**
   * Seeds empty purpose fields when new rows appear (e.g. Paid to → customer).
   * Settlement rows stay in sync with `getDefaultSettlePurpose` (e.g. when the
   * creditor name changes) until the user edits that purpose field.
   */
  useEffect(() => {
    const prev = getValues("cashOuts") ?? {};
    let next: Record<string, string | undefined> = prev;
    let changed = false;

    for (const row of cashOutRows) {
      if (purposeEditedByUserRef.current.has(row.key)) {
        continue;
      }
      const current = prev[row.key];
      if (row.isRemaining) {
        if (current != null && String(current).trim() !== "") {
          continue;
        }
      }
      if (prev[row.key] === row.defaultPurpose) {
        continue;
      }
      if (!changed) {
        next = { ...prev };
        changed = true;
      }
      next[row.key] = row.defaultPurpose;
    }

    if (changed) {
      setValue("cashOuts", next, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [cashOutRows, getValues, setValue]);

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-md mb-1 font-semibold">Cash Out</h3>
        <p className="mb-2 text-sm text-brand-copy-light">
          If any funds are to be paid directly to the applicant rather than to a
          creditor, please confirm the details below.
        </p>
      </div>

      <div className="mb-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th scope="col" className={`${TH_STYLE} bg-brand-yellow-light`}>
                  Purpose
                </th>
                <th
                  scope="col"
                  className={`${TH_STYLE} bg-brand-yellow-lighter`}
                >
                  Amount
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {cashOutRows.map((row: CashOutRow) => {
                return (
                  <tr key={row.key}>
                    <td
                      className={`${TD_STYLE} min-w-[300px] bg-brand-yellow-light bg-opacity-25 pl-3`}
                    >
                      <Controller
                        control={control}
                        name={`cashOuts.${row.key}` as `cashOuts.${string}`}
                        render={({ field, fieldState }) => (
                          <div>
                            <input
                              className={STYLE_INPUT}
                              placeholder={
                                row.isRemaining
                                  ? "e.g. Home improvements"
                                  : "e.g. To settle PayPal"
                              }
                              value={field.value ?? ""}
                              onChange={(e) => {
                                purposeEditedByUserRef.current.add(row.key);
                                field.onChange(e.target.value);
                              }}
                              onBlur={field.onBlur}
                              name={field.name}
                              ref={field.ref}
                              required
                              aria-invalid={fieldState.invalid}
                              aria-required
                            />
                            <FormInputError error={fieldState.error?.message} />
                          </div>
                        )}
                      />
                    </td>
                    <td
                      className={`${TD_STYLE} bg-brand-yellow-lighter bg-opacity-25 pr-3`}
                    >
                      <div className={`${row.isRemaining ? "mt-0" : "mt-2"}`}>
                        <span className="text-base font-semibold text-brand-orange-medium">
                          £{asMoney(row.amount, true)}
                        </span>
                        {row.isRemaining && (
                          <div className="mt-0.5 text-sm text-brand-copy-lighter">
                            Remaining cash balance
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {cashOutRows.length === 0 && (
                <tr>
                  <td
                    colSpan={2}
                    className={`${TD_STYLE} py-6 text-center text-brand-copy-light`}
                  >
                    No cash out - all funds allocated to creditors
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
